import React, { useEffect, useState } from 'react';

import Icon from '../Icon';

const scriptPromises = {};
const gapiLoadCheckIntervalMs = 50;
const gapiLoadCheckTimeoutMs = 5000;

const loadExternalScript = (src, isReady = () => false) => {
  if (scriptPromises[src]) return scriptPromises[src];

  scriptPromises[src] = new Promise((resolve, reject) => {
    const bindScriptListeners = script => {
      const onLoad = () => {
        script.dataset.loaded = 'true';
        delete script.dataset.loadFailed;
        resolve();
      };

      const onError = () => {
        script.dataset.loadFailed = 'true';
        script.remove();
        reject(new Error(`Failed to load script: ${src}`));
      };

      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });
    };

    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true' || isReady()) {
        existing.dataset.loaded = 'true';
        delete existing.dataset.loadFailed;
        resolve();
        return;
      }

      if (existing.dataset.loadFailed === 'true') {
        existing.remove();
      } else {
        bindScriptListeners(existing);
        return;
      }
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    bindScriptListeners(script);
    document.body.appendChild(script);
  }).catch(error => {
    delete scriptPromises[src];
    throw error;
  });

  return scriptPromises[src];
};

const waitForGapiLoad = () =>
  new Promise((resolve, reject) => {
    if (typeof window.gapi?.load === 'function') {
      resolve();
      return;
    }

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (typeof window.gapi?.load === 'function') {
        window.clearInterval(timer);
        resolve();
        return;
      }

      if (Date.now() - startedAt >= gapiLoadCheckTimeoutMs) {
        window.clearInterval(timer);
        reject(new Error('window.gapi.load is not available after script load'));
      }
    }, gapiLoadCheckIntervalMs);
  });

/**
 * Hey there! This is a Google Drive picker for your Phoenix app.
 *
 * This little React component lets users pick files or folders from their Google Drive.
 * It plays nicely with Phoenix LiveView in your Elixir app.
 *
 * How it works with Phoenix:
 *
 * First, Phoenix renders this component and passes it some data.
 * When someone picks a file/folder, this component tells Phoenix about it.
 * Then Phoenix does its thing with that information.
 *
 * The data goes both ways:
 *
 * From Phoenix to here: We get initial values, the access token, and settings
 * From here to Phoenix: We send back the file/folder info with its ID and other details
 *
 * What each parameter does:
 * - pushEventTo: Lets us talk back to Phoenix
 * - value: The currently selected file/folder (if any)
 * - onChange: The name of the event to trigger when selection changes
 * - target: The ID of the HTML element we're attached to
 * - readonly: If true, users can't change the selection
 * - access_token: The Google token that lets us access Drive
 * - type: Predefined filter type - "directories" for folders only, "sheets" for Google Sheets only, "documents" for Google Docs only
 * - error: Error message to display if there's an issue
 */
export function GooglePicker({
  pushEventTo,
  value,
  onChange,
  target,
  readonly,
  access_token,
  app_id,
  type, // "directories", "sheets", or "documents"
  error,
}) {
  const targetEl = document.getElementById(target);
  const [pickerReady, setPickerReady] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Include some override for styles in the picker that Google loads.
   * We keep them contained here and add them only when necessary.
   */
  useEffect(() => {
    // Check if we already added the style tag
    if (document.querySelector('style[data-google-picker]')) return;

    const styleTag = document.createElement('style');
    styleTag.setAttribute('data-google-picker', ''); // marker to find later
    styleTag.textContent = `
      /* Google Picker overrides */
      .picker-dialog {
        border: none !important;
        border-radius: 1rem !important;
        box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(63, 63, 70, 0.1) 0px 20px 25px -5px, rgba(63, 63, 70, 0.1) 0px 8px 10px -6px !important;
      }
      .picker-dialog-bg {
        background: rgba(63, 63, 70, 0.05) !important;
      }
    `;
    document.head.appendChild(styleTag);

    // Optional cleanup on unmount (remove the tag)
    return () => {
      styleTag.remove();
    };
  }, []);

  /**
   * This loads all the Google stuff we need
   *
   * When our component first appears, we need to grab Google's scripts.
   * The nice thing is, in Phoenix, this only happens when the LiveView
   * component that contains us is actually on the page.
   */
  useEffect(() => {
    if (readonly) return;

    let isMounted = true;

    const initGooglePicker = async () => {
      try {
        await loadExternalScript('https://accounts.google.com/gsi/client', () => Boolean(window.google?.accounts));
        await loadExternalScript('https://apis.google.com/js/api.js', () => typeof window.gapi?.load === 'function');
        await waitForGapiLoad();

        if (!isMounted) return;

        window.gapi.load('picker', () => {
          if (isMounted) setPickerReady(true);
        });
      } catch (initError) {
        console.error('Failed to initialize Google Picker scripts', initError);
      }
    };

    initGooglePicker();

    return () => {
      isMounted = false;
    };
  }, [readonly]);

  /**
   * This keeps our file/folder selection in sync with Phoenix
   *
   * When Phoenix sends us a new file/folder value, this makes sure we update to match.
   * It's like having a conversation with the server - Phoenix says "here's the item now"
   * and we say "got it, updating my display!"
   */
  useEffect(() => {
    if (value && typeof value === 'object') {
      setSelectedItem(Array.isArray(value) ? value[0] : value);
    } else if (value === null || value === undefined) {
      setSelectedItem(null);
    }
  }, [value]);

  /**
   * This is where the magic happens - opening Google's file/folder picker
   *
   * Here's what this function does:
   * 1. Makes sure everything's ready to go
   * 2. Configures the picker based on the type filter
   * 3. Pops up Google's picker dialog
   * 4. Grabs what the user selected
   * 5. Tells Phoenix about it
   *
   * The cool part: When someone picks a file/folder, we package it up nicely
   * with a special 'value' field containing the ID, which is exactly
   * what Phoenix needs. It's like translating between Google-speak and Phoenix-speak!
   */
  const openPicker = () => {
    if (readonly || error || !pickerReady || !access_token) {
      console.error('Cannot open picker: not ready, has error, or no access token.');
      return;
    }

    if (!window.google?.picker) {
      console.error('Google Picker API not available.');
      return;
    }

    setIsLoading(true);

    try {
      let view;

      // Configure view based on type filter
      if (type === 'directories') {
        // Show only folders for directory selection
        view = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
          .setIncludeFolders(true)
          .setSelectFolderEnabled(true);
      } else if (type === 'sheets') {
        // Show only Google Sheets
        view = new window.google.picker.DocsView(window.google.picker.ViewId.SPREADSHEETS);
      } else if (type === 'documents') {
        // Show only Google Docs
        view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCUMENTS);
      } else {
        // Default fallback - show all files and folders
        view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS).setIncludeFolders(true);
      }

      const picker = new window.google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(access_token)
        .setAppId(app_id)
        .setCallback(data => {
          if (data.action === window.google.picker.Action.PICKED) {
            const item = data.docs[0];

            // Let's make sure Phoenix gets exactly what it needs
            const itemWithId = {
              ...item,
              value: item.id, // This is the magic key Phoenix is looking for!
            };

            setSelectedItem(itemWithId);
            // Here's where we shout back to Phoenix: "Hey, they picked something!"
            // This is how we send the good news back to the server
            pushEventTo?.(targetEl, onChange, { value: itemWithId });
          }
          setIsLoading(false);
        })
        .build();

      picker.setVisible(true);
    } catch (err) {
      console.error('Failed to open Google Picker', err);
      setIsLoading(false);
    }
  };

  /**
   * This handles the "oops, I didn't mean to pick that file/folder" scenario
   *
   * When someone clicks the "Remove" button, this function:
   * 1. Clears our local memory of the selected item
   * 2. Tells Phoenix "hey, they changed their mind!"
   *
   * It's like hitting the reset button - we send a null value to Phoenix
   * which is our way of saying "nothing to see here anymore!"
   */
  const clearSelection = () => {
    setSelectedItem(null);
    // Tell Phoenix "forget that item I mentioned earlier!"
    pushEventTo?.(targetEl, onChange, { value: null });
  };

  const getNoSelectionText = () => {
    if (type === 'directories') {
      return 'No folder selected';
    } else if (type === 'sheets') {
      return 'No Google Sheet selected';
    } else if (type === 'documents') {
      return 'No Google Doc selected';
    } else {
      return 'No file selected';
    }
  };

  return (
    <div className='flex w-full flex-row'>
      {selectedItem ? (
        <div data-field-id={selectedItem.id} className='my-auto'>
          <div className='mention mx-0.5 flex items-center overflow-hidden rounded bg-indigo-100 ring-1 ring-indigo-100'>
            <div className='flex w-full items-center truncate'>
              <div className='text-base-700 flex min-h-[28px] items-center gap-x-2 rounded bg-white py-1 pl-1.5 pr-2'>
                <div className='flex h-4 w-4 flex-shrink-0 items-center justify-center'>
                  {selectedItem.mimeType == 'application/vnd.google-apps.folder' && (
                    <Icon name='hero-folder' className='text-base-500 h-4 w-4' />
                  )}
                  {selectedItem.mimeType != 'application/vnd.google-apps.folder' && (
                    <Icon name='hero-document-text' className='text-base-500 h-4 w-4' />
                  )}
                </div>
                <span className='flex items-center truncate leading-none'>{selectedItem.name}</span>
              </div>
            </div>
            {!readonly && (
              <div
                className='ml-px flex cursor-pointer items-center rounded px-1 py-1 hover:bg-indigo-200'
                onClick={clearSelection}
              >
                <Icon name='hero-x-mark' className='text-base-700 h-3 w-3' />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={error ? 'text-red-600' : 'text-gray-500'}>{error ? error : getNoSelectionText()}</div>
      )}

      {!readonly && !selectedItem && !error && (
        <button
          type='button'
          onClick={openPicker}
          disabled={!pickerReady || !access_token || isLoading}
          className='text-base-900 hover:bg-base-100 ring-base-300 disabled ml-auto inline-flex cursor-pointer items-center justify-center rounded-md bg-white px-3 py-2 text-xs font-semibold opacity-50 shadow-sm ring-1 ring-inset focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 active:text-black/80 disabled:opacity-50'
        >
          {isLoading ? 'Loading...' : 'Browse'}
        </button>
      )}

      {!pickerReady && !readonly && !error && (
        <div className='mt-1 text-sm text-gray-500'>Loading Google Drive Picker...</div>
      )}
    </div>
  );
}

// Global guards for types
if (typeof window !== 'undefined') {
  window.google = window.google || {};
  window.gapi = window.gapi || {};
}
